import { useMutation, useQuery } from '@tanstack/react-query';
import {
    calculateBidding,
    createBiddingCalculatedTotals,
    getBiddingCalculated,
    getBiddingCalculatedResults,
    getLastBiddingCalculatedTotalsReleaseDate,
    getLastBiddingCalculationDate,
    getSumOfReleasedBiddingCalculatedTotals,
} from '../../services/biddingCalculated';
import { QueryKey } from '../../enums/QueryKey';
import type { Dayjs } from '@utils/dayjs';
import { formatLastUploadDate, getStartAndEndOfAMonth } from '../../utils/dateUtils';
import { queryClient } from '../../services/queryClient';
import { useDispatchSuccessNotification } from '../useDispatchSuccessNotification';
import { useDispatchErrorNotification } from '../useDispatchErrorNotification';
import { calculateCorrectedResults } from '../../services/correctedResults';
import { getCorrectedResultsQueryKey } from './correctedResults';

export function getBiddingCalculatedQueryKey({
    date,
    workingUnitLocation,
    released,
}: {
    date: string | Dayjs | Date;
    workingUnitLocation: number;
    released?: boolean;
}) {
    const { dateFrom, dateTo } = getStartAndEndOfAMonth(date);
    return [QueryKey.BiddingCalculated, { dateFrom: dateFrom.toISOString(), dateTo: dateTo.toISOString(), workingUnitLocation }, released];
}

export function useBiddingCalculated({
    date,
    workingUnitLocation,
    released,
}: {
    date: string | Dayjs | Date;
    workingUnitLocation: number;
    released?: boolean;
}) {
    const { dateFrom, dateTo } = getStartAndEndOfAMonth(date);

    const { data, ...rest } = useQuery({
        queryKey: getBiddingCalculatedQueryKey({ date, workingUnitLocation, released }),
        queryFn: () => getBiddingCalculated({ dateFrom: dateFrom.toISOString(), dateTo: dateTo.toISOString(), workingUnitLocation, released }),
        enabled: !!date && !!workingUnitLocation,
        refetchOnWindowFocus: false,
    });

    return {
        biddingCalculated: data || [],
        data: data || [],
        ...rest,
    };
}

export function getBiddingCalculatedResultsQueryKey({
    date,
    workingUnitLocation,
}: {
    date: string | Dayjs | Date;
    workingUnitLocation: number;
}) {
    const { dateFrom, dateTo } = getStartAndEndOfAMonth(date);
    return [QueryKey.BiddingCalculatedResults, { dateFrom: dateFrom.toISOString(), dateTo: dateTo.toISOString(), workingUnitLocation }];
}

export function useBiddingCalculatedResults({
    date,
    workingUnitLocation,
}: {
    date: string | Dayjs | Date;
    workingUnitLocation: number;
}) {
    const { dateFrom, dateTo } = getStartAndEndOfAMonth(date);

    const { data, ...rest } = useQuery({
        queryKey: getBiddingCalculatedResultsQueryKey({ date, workingUnitLocation }),
        queryFn: () => getBiddingCalculatedResults({ dateFrom: dateFrom.toISOString(), dateTo: dateTo.toISOString(), workingUnitLocation }),
        enabled: !!date && !!workingUnitLocation,
        refetchOnWindowFocus: false,
    });

    return {
        data: data || [],
        ...rest,
    };
}

export function useLastBiddingCalculationDate(workingUnitLocation: number) {
    return useQuery({
        queryKey: [QueryKey.LastBiddingCalculationDate, { workingUnitLocation }],
        queryFn: () => getLastBiddingCalculationDate({ workingUnitLocation }),
        enabled: !!workingUnitLocation,
        refetchOnWindowFocus: false,
    });
}

export function useLastBiddingCalculatedTotalsReleaseDate(workingUnitLocation: number) {
    const { data } = useQuery({
        queryKey: [QueryKey.LastBiddingCalculatedTotalsReleaseDate, { workingUnitLocation }],
        queryFn: () => getLastBiddingCalculatedTotalsReleaseDate({ workingUnitLocation }),
        enabled: !!workingUnitLocation,
        refetchOnWindowFocus: false,
    });

    return data ? formatLastUploadDate(data) : null;
}

export function useSumOfReleasedBiddingCalculatedTotals({
    date,
    workingUnitLocation,
}: {
    date: string | Dayjs | Date;
    workingUnitLocation: number;
}) {
    const { dateFrom, dateTo } = getStartAndEndOfAMonth(date);
    const dateFromStr = dateFrom.toISOString();
    const dateToStr = dateTo.toISOString();

    const { data: sumOfReleasedBiddingCalculatedTotals } = useQuery<number>({
        queryKey: [QueryKey.ReleasedBiddingCalculatedTotalsSum, { dateFrom: dateFromStr, dateTo: dateToStr, workingUnitLocation }],
        queryFn: () => getSumOfReleasedBiddingCalculatedTotals({ dateFrom: dateFromStr, dateTo: dateToStr, workingUnitLocation }),
        initialData: 0,
        enabled: !!date && !!workingUnitLocation,
    });

    return { sumOfReleasedBiddingCalculatedTotals };
}

export function useUpdateTotals({
    selectedDate,
    selectedWorkingUnitLocation,
}: {
    selectedDate: string | Dayjs | Date;
    selectedWorkingUnitLocation: number;
}) {
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();
    const { dispatchErrorNotification } = useDispatchErrorNotification();

    return useMutation({
        mutationFn: createBiddingCalculatedTotals,
        onSuccess: () => {
            queryClient.invalidateQueries(
                getBiddingCalculatedQueryKey({
                    date: selectedDate,
                    workingUnitLocation: selectedWorkingUnitLocation,
                })
            );
            dispatchSuccessNotification({
                message: 'Die Änderungen wurden gespeichert',
            });
        },
        onError: (error: Error) => {
            dispatchErrorNotification({
                message: error.message || 'Fehler beim Speichern der Änderungen',
            });
        },
    });
}

export function useCalculateCorrectedResults({
    selectedDate,
    selectedWorkingUnitLocation,
    onSettled,
}: {
    selectedDate: Dayjs;
    selectedWorkingUnitLocation: number;
    onSettled: () => void;
}) {
    return useMutation({
        mutationFn: calculateCorrectedResults,
        onSuccess: () => {
            // Invalidate all queries with these prefixes for the selected date/workingUnitLocation
            queryClient.invalidateQueries(
                getCorrectedResultsQueryKey({ date: selectedDate, workingUnitLocation: selectedWorkingUnitLocation })
            );
            queryClient.invalidateQueries(
                getBiddingCalculatedResultsQueryKey({
                    date: selectedDate,
                    workingUnitLocation: selectedWorkingUnitLocation,
                })
            );
            queryClient.invalidateQueries({
                queryKey: [QueryKey.LastBiddingCalculationDate, { workingUnitLocation: selectedWorkingUnitLocation }],
            });
        },
        onSettled,
    });
}

export function useCalculateBidding() {
    return useMutation({
        mutationFn: calculateBidding,
    });
}
